import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import { mapParamsBySchema, mapRowsBySchema } from '../../../../util';
import { RIS_BASIC_01_Schema } from './RIS_BASIC_01_Schema';
import { apiRequest } from '../../../../Services/Common/ApiRequest';
import { handleQuerySuccess } from '../../../../hooks/useQuriesSuccess';
import useSearchMutaion from './../../../../hooks/apis/useSearchMutaion';
import useDeleteMutation from '../../../../hooks/apis/useDeleteMutaion';
import useSaveMutation from '../../../../hooks/apis/useSaveMutaion';

/**
 * RIS_BASIC_01 React Query 훅
 * 위험성평가(RIS_BASIC_01) 모듈의 중앙화된 API 호출 훅
 * MEM_BASIC 패턴을 따르는 스키마 기반 API 통합
 *
 * 참고: 이 모듈은 표준 apiRequest 함수 대신 커스텀 Commons.Axios 래퍼를 사용합니다.
 * 이는 기존 인프라와의 호환성을 유지하기 위함입니다.
 */

/**
 * =================
 * 조회/쿼리 훅
 * =================
 */

/**
 * useSearch1 - 기본 위험성평가 목록 조회
 */
export const useSearch1 = (options = {}) => {
  const { onSuccess } = options;
  const params = mapParamsBySchema(RIS_BASIC_01_Schema.mapper.search1);

  const query = useQuery({
    queryKey: ['RiskEvaluation', 'getRisBasic01R1', params],
    queryFn: () =>
      apiRequest('post', Global.urls.RiskEvaluation.getRisBasic01R1, params),
    enabled: true,
  });

  useEffect(() => {
    if (!query.isSuccess) return;
    handleQuerySuccess(query.data, {
      onSuccess: async (rows) => {
        await onSuccess?.(rows);
      },
    });
  }, [query.isSuccess, query.dataUpdatedAt]);

  return query;
};

/**
 * useSearch2 - 안전보건정보 조회
 */
export const useSearch2 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      RIS_BASIC_01_Schema.mapper.search2,
      context
    );
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisBasic01R2,
      params
    );
  }, successOptions);
};

/**
 * useSearch3 - 부서조회
 */
export const useSearch3 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      RIS_BASIC_01_Schema.mapper.search3,
      context
    );
    return apiRequest('post', Global.urls.Utility.getDepLast, params);
  }, successOptions);
};
/**
 * useSearchR3 - 부서별 공정 조회 (SearchLogic4 대체)
 * 부서별 공정 조회 (getRisBasic01R3)
 */
export const useSearch4 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      RIS_BASIC_01_Schema.mapper.search4,
      context
    );
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisBasic01R3,
      params
    );
  }, successOptions);
};

/**
 * useSearchR5 - 유해·위험 기계·기구 통합 조회
 */
export const useSearch5 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      RIS_BASIC_01_Schema.mapper.search5,
      context
    );
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisBasic01R4,
      params
    );
  }, successOptions);
};
/**
 * useSearchR6 - 위험성 평가 실시 안 작업목록 조회
 */
export const useSearch6 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      RIS_BASIC_01_Schema.mapper.search6,
      context
    );
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisBasic01R5,
      params
    );
  }, successOptions);
};
/**
 * useSearchR6 - 위험성 평가 실시 안 표준모델 요인 조회
 */
export const useSearch7 = (successOptions) => {
  return useSearchMutaion((context) => {
    const params = mapParamsBySchema(
      RIS_BASIC_01_Schema.mapper.search7,
      context
    );
    return apiRequest(
      'post',
      Global.urls.RiskEvaluation.getRisBasic01R6,
      params
    );
  }, successOptions);
};

/**
 * GetRisBasic01R6 응답을 표준모델 모달 그리드용으로 정규화합니다.
 * 서버에서 AddActnItems(목록)와 AddActnCn(원문)을 내려주며, 표시는 addActnDisplay로 통일합니다.
 */
export const NormalizeGetRisBasic01R6Rows = (rows) => {
  const arr = Array.isArray(rows) ? rows : [];
  return arr.map((r, i) => {
    const addActnItems = r.addActnItems ?? r.AddActnItems;
    const addActnCn = r.addActnCn ?? r.AddActnCn ?? '';
    const addActnDisplay =
      Array.isArray(addActnItems) && addActnItems.length > 0
        ? addActnItems.join('\n')
        : String(addActnCn || '').trim();
    const sttLprvsTtlNm = String(r.sttLprvsTtlNm ?? r.SttLprvsTtlNm ?? '').trim();
    const sttLprvsNo = String(r.sttLprvsNo ?? r.SttLprvsNo ?? '').trim();
    const condolenceNo = String(
      r.condolenceNo ?? r.CondolenceNo ?? sttLprvsNo
    ).trim();
    return {
      ...r,
      no: i + 1,
      addActnDisplay,
      sttLprvsTtlNm,
      sttLprvsNo,
      condolenceNo,
      categoryComment: String(
        r.categoryComment ?? r.CategoryComment ?? ''
      ).trim(),
      depNm: String(r.depNm ?? r.DepNm ?? '').trim(),
    };
  });
};

/**
 * =================
 * 저장 뮤테이션 훅
 * =================
 */

/**
 * useSave1 - 위험성평가 기본정보 저장
 */
export const useSave1 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(rows, RIS_BASIC_01_Schema.mapper.save1, {
      context,
    });
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisBasic01U1,
      payload
    );
  }, options);
};
/**
 * useSave2 - 위험성평가 기본정보 안 공정등록 저장
 */
export const useSave2 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(rows, RIS_BASIC_01_Schema.mapper.save2, {
      context,
    });
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisBasic01U2,
      payload
    );
  }, options);
};
/**
 * useSave2 - 위험성평가 실시 저장
 */
export const useSave3 = (options = {}) => {
  return useSaveMutation(async (rows, context) => {
    const payload = mapRowsBySchema(rows, RIS_BASIC_01_Schema.mapper.save3, {
      context,
    });
    // 저장 API 전송 직전 payload 로그 (암호화된 값 포함)
    if (Array.isArray(payload) && payload.length > 0) {
      payload.forEach((item) => {
        console.log('저장클릭시 쿼리파람', item);
      });
    }
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.saveRisBasic01U3,
      payload
    );
  }, options);
};
/**
 * =================
 * 삭제 뮤테이션 훅
 * =================
 */

/**
 * useDelete1 - 위험성평가 삭제
 */
export const useDelete1 = (options = {}) => {
  return useDeleteMutation(async (rows) => {
    const payload = mapRowsBySchema(rows, RIS_BASIC_01_Schema.mapper.delete1);
    return apiRequest(
      'post1',
      Global.urls.RiskEvaluation.deleteRisBasic01D1,
      payload
    );
  }, options);
};

/**
 * 모든 훅 내보내기
 */
export default {
  // 조회 훅
  useSearch1,
  useSearch2,
  useSearch3,
  useSearch4,
  useSearch5,

  // 저장 훅
  useSave1,

  // 삭제 훅
  useDelete1,
};
